/* eslint-disable import/no-named-as-default-member */
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FormControlLabel,
  GKButton,
  GKDatePicker,
  GKModal,
  GKRadio,
  GKSelect,
  Grid2,
  MenuItem,
  RadioGroup,
  ReloadIcon,
  SendIcon,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useMutation } from "hooks/useQueries";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import { KYC_OPTIONS } from "utils/constants";
import { refetchQuery } from "utils/helpers";
import { clientKycModalFormSchema } from "utils/validationSchema";

interface Props {
  showData?: any;
  refetch?: any;
  clientData?: any;
  portfolioDetails?: any;
  includePortfolio?: boolean;
}

type FormValues = {
  pan?: string;
  identification_type?: string;
  kyc_status?: string;
  sendOptions?: string;
  dateOfBirth?: string | null;
};

const ComplianceKYCModal = ({
  setOpen,
  open,
  showData,
  refetch,
  clientData,
  portfolioDetails,
  includePortfolio,
}: Props & GKModalProps) => {
  const { id } = useParams();

  const isDataPresent = Boolean(showData?.pan);

  const userInfo = useAppSelector((state) => state?.user?.userData);

  const { mutate: createKyc, isLoading } = useMutation({
    mutationKey: ["CREATE_KYC"],
    mutationFn: (data) => axiosInstance.post(`/kyc/${id}/`, data),
    onSuccess: () => {
      toast.success("KYC request created successfully!");
      refetch?.();
      refetchQuery("GET_ALL_COMPLIANCE_BY_CLIENT_ID");
      setOpen(false);
    },
  });

  const { mutate: fetchKRA, isLoading: KRALoading } = useMutation({
    mutationKey: ["FETCH_KRA"],
    mutationFn: (data) => axiosInstance.post("/pan-details/", data),
    onSuccess: () => {
      toast.success("KRA fetched successfully!");
      refetch?.();
      setOpen(false);
    },
  });

  const { mutate: updateKyc, isLoading: updateKycLoading } = useMutation({
    mutationKey: ["UPDATE_KYC"],
    mutationFn: (data) => axiosInstance.post(`/resendKyc/resendKYC/`, data),
    onSuccess: () => {
      toast.success("KYC updated successfully!");
      refetch?.();
      setOpen(false);
    },
  });

  const form = useForm<FormValues>({
    defaultValues: {
      pan:
        (isDataPresent ? showData?.pan : clientData?.personalDetails?.pan) ||
        portfolioDetails?.pan ||
        "",
      identification_type:
        (isDataPresent && showData?.identificationType) || "",
      kyc_status: (isDataPresent && showData?.kycStatus) || "",
      sendOptions: (isDataPresent && showData?.sendOptions) || "Mobile",
      dateOfBirth:
        (isDataPresent && showData?.dateOfBirth) ||
        clientData?.personalDetails?.dateOfBirth ||
        portfolioDetails?.dateOfBirth ||
        null,
    },
    resolver: yupResolver(clientKycModalFormSchema),
  });

  const selectedKycType = form.watch("identification_type");

  const onSubmit = async (values: FormValues) => {
    if (selectedKycType === "KRA FETCH") {
      const data: any = {
        pan: values.pan,
        dob: values.dateOfBirth,
        phone: clientData?.phone || portfolioDetails?.phone,
        company: userInfo?.companyId || portfolioDetails?.company,
        clientId: clientData?.id || portfolioDetails?.clientId,
      };
      if (includePortfolio) {
        data.portfolioId = portfolioDetails?.id;
      }
      fetchKRA(data);
    } else if (isDataPresent) {
      const data: any = {
        kyc_id: showData?.id,
        identification_type: values.identification_type,
        sendOptions: values.sendOptions,
      };
      updateKyc(data);
    } else {
      const data: any = {
        client: id,
        pan: values.pan,
        identificationType: values.identification_type,
        kycStatus: values.kyc_status,
        sendOptions: values.sendOptions,
      };
      createKyc(data);
    }
  };

  return (
    <GKModal
      width={"350px"}
      open={open}
      setOpen={setOpen}
      modalTitle={"Client KYC"}
      footer={
        <Grid2 container justifyContent="flex-end" gap={1}>
          <GKButton
            sx={{ width: "155px" }}
            variant="outlined"
            size="medium"
            onClick={() => setOpen(false)}
          >
            Cancel
          </GKButton>
          {selectedKycType === "KRA FETCH" ? (
            <GKButton
              sx={{ width: "155px" }}
              type="submit"
              variant="contained"
              form="kyc-form"
              size="medium"
              loading={isLoading || updateKycLoading || KRALoading}
              startIcon={<ReloadIcon />}
            >
              {KRALoading ? "Fetching" : "Fetch KRA"}
            </GKButton>
          ) : (
            <GKButton
              sx={{ width: "155px" }}
              type="submit"
              variant="contained"
              form="kyc-form"
              size="medium"
              loading={isLoading || updateKycLoading}
              startIcon={<SendIcon />}
            >
              {isDataPresent ? "Sending" : "Send Link"}
            </GKButton>
          )}
        </Grid2>
      }
    >
      <form id="kyc-form" onSubmit={form.handleSubmit(onSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2
            container
            size={12}
            p={1.5}
            borderRadius={2}
            bgcolor={"var(--grey-bg-100)"}
            gap={1}
          >
            <Grid2 size={12} display="flex" alignItems="flex-start">
              <Typography fontSize={14} fontWeight={500} color="#222">
                {clientData?.personalDetails?.name ||
                  portfolioDetails?.name ||
                  "--"}
              </Typography>
            </Grid2>
            <Grid2 size={12} display="flex" alignItems="center">
              <Typography
                fontSize={12}
                fontWeight={600}
                color="#697586"
                sx={{ mr: 1 }}
              >
                PAN:
              </Typography>
              <Typography fontSize={12} fontWeight={500} color="#222">
                {form.watch("pan") ||
                  clientData?.personalDetails?.pan ||
                  portfolioDetails?.pan ||
                  "--"}
              </Typography>
            </Grid2>
          </Grid2>

          <Grid2 container size={12}>
            <Controller
              name="identification_type"
              control={form.control}
              render={({ field, formState: { errors } }) => (
                <GKSelect
                  {...field}
                  inputLabel="KYC Type"
                  error={Boolean(errors?.identification_type)}
                  helperText={
                    Boolean(errors?.identification_type) &&
                    `${errors?.identification_type?.message}`
                  }
                >
                  <MenuItem style={{ display: "none" }} value="">
                    Select Identification Type
                  </MenuItem>
                  {KYC_OPTIONS?.map((data) => (
                    <MenuItem value={data?.value} key={data?.value}>
                      {data?.name}
                    </MenuItem>
                  ))}
                </GKSelect>
              )}
            />
          </Grid2>

          {selectedKycType !== "KRA FETCH" && (
            <Grid2 size={12}>
              <Typography fontSize={14} fontWeight={500}>
                Send KYC via
              </Typography>
              <Grid2 display="flex" gap={3}>
                <Controller
                  name="sendOptions"
                  control={form.control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      value={field.value || "Mobile"}
                      onChange={(e) => field.onChange(e.target.value)}
                      sx={{ display: "flex", flexDirection: "row", gap: 0.5 }}
                    >
                      <FormControlLabel
                        value="Mobile"
                        control={<GKRadio />}
                        label="Mobile"
                      />
                      <FormControlLabel
                        value="Email"
                        control={<GKRadio />}
                        label="Email"
                      />
                    </RadioGroup>
                  )}
                />
              </Grid2>
            </Grid2>
          )}

          {selectedKycType === "KRA FETCH" && (
            <Grid2 size={12}>
              <Controller
                name="dateOfBirth"
                control={form.control}
                render={({ field }) => (
                  <GKDatePicker
                    {...field}
                    inputLabel="Date of Birth"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) =>
                      field.onChange(
                        value ? dayjs(value).format("YYYY-MM-DD") : "",
                      )
                    }
                    minDate={dayjs("1901-01-01")}
                    slotProps={{
                      textField: {
                        error: false,
                        helperText: false,
                      },
                    }}
                  />
                )}
              />
            </Grid2>
          )}
        </Grid2>
      </form>
    </GKModal>
  );
};

export default ComplianceKYCModal;
